import get from "lodash/get";
import { call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { matchPath } from 'connected-react-router';
import { LOCATION_CHANGE, getLocation } from "connected-react-router";
import { actions, constants, selector, routeConfig } from "./redux";
import { addItem as notify } from "../notifications/actions";
import Api from "../../utils/Api";
import { didDocumentLocationChange } from "../../utils/router";
import { addSuccess, addError } from "../notifications/actions";
import apiErrorMessage from "../../utils/Api/apiErrorMessage";
// (stopSubmit removed in Epic 13f — per-field error display now via RHF setError; snackbar covers catch-all.)
export function* fetchItem() {
  try {
    const { pathname } = yield select(getLocation);
    const {
      params: { id },
    } = matchPath(pathname, routeConfig);
    const featureState = yield select(selector);
    const data = yield call(Api.vessels.show, id, featureState);

    yield put(actions.addLoadedItem(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemError(e));
  }
}

export function* fetchItemsPageInit(action) {
  const match = didDocumentLocationChange(action, routeConfig);

  if (match) {
    yield put(actions.loadItemRequest(match.params.id));
  }
}

// What the backend found, in words. `not_found` is not a failure — the source
// simply has no photo of this vessel — so it is not shown as an error.
const PHOTO_MESSAGES = {
  attached: ['Vessel photo updated', 'Success'],
  unchanged: ['The photo is already the latest one', 'Up to date'],
  not_found: ['There is no photo of this vessel on VesselFinder yet', 'No photo'],
};

export function* updatePhoto() {
  try {
    yield put(actions.setPhotoIsLoading(true));
    const { id } = yield select((s) => s.vesselDetails.data);
    const result = yield call(Api.vessels.updatePhoto, { id });

    const [message, title] = PHOTO_MESSAGES[result?.status] || PHOTO_MESSAGES.attached;
    yield put(addSuccess(message, title));
    if (result?.status === 'attached') {
      yield put(actions.photoArrived({
        id, photo: result.photo, photo_credit: result.photo_credit, photo_pending: false,
      }));
    }
  } catch (error) {
    console.error(error);
    yield put(addError(apiErrorMessage(error, 'Could not get the photo. Please try again.'), 'Photo not updated'));
  } finally {
    // It used to be cleared on success only: one refused request left the
    // button spinning until the page was reloaded.
    yield put(actions.setPhotoIsLoading(false));
  }
}

function* setManager({ payload }) {
  try {
    const { id: vesselId, manager_vessel } = yield select(
      (s) => s.vesselDetails.data
    );

    if (manager_vessel && payload) {
      yield call(Api.vessels.changeManager, manager_vessel.id, payload.value);
    } else if (!manager_vessel && payload) {
      yield call(Api.vessels.setManager, vesselId, payload.value);
    } else if (!payload && manager_vessel.id) {
      yield call(Api.vessels.deleteManager, manager_vessel.id);
    } else {
      throw new Error("Internal front-end error #666");
    }

    yield put(actions.managerChangedSuccessfully());
    yield put(
      notify({
        type: "success",
        title: "Success",
        message: payload
          ? `${payload.label} is set as manager successfully`
          : "The manager deleted successfully",
      })
    );
  } catch (e) {
    console.error(e);
  }
}

function* setOwner({ payload }) {
  try {
    const { id: vesselId, owner_vessel } = yield select(
      (s) => s.vesselDetails.data
    );

    if (owner_vessel && payload) {
      yield call(Api.vessels.changeOwner, owner_vessel.id, payload.value);
    } else if (!owner_vessel && payload) {
      yield call(Api.vessels.setOwner, vesselId, payload.value);
    } else if (!payload && owner_vessel.id) {
      yield call(Api.vessels.deleteOwner, owner_vessel.id);
    } else {
      throw new Error("Internal front-end error #999");
    }

    yield put(actions.ownerChangedSuccessfully());
    yield put(
      notify({
        type: "success",
        title: "Success",
        message: payload
          ? `${payload.label} is set as owner successfully`
          : "The owner deleted successfully",
      })
    );
  } catch (e) {
    console.error(e);
  }
}

function* changeCoordinator({ payload }) {
  try {
    const { id: vesselId } = yield select((s) => s.vesselDetails.data);
    const { label: coordinatorName, value: coordinator_id, relation } = payload;
    const data = { id: coordinator_id, type: "clients" };

    yield call(Api.vessels.updateRelation, vesselId, relation, data);

    yield put(actions.loadItemRequest());

    yield put(
      notify({
        type: "success",
        title: "Success",
        message: coordinatorName
          ? `${coordinatorName} is set as Crew Coordinator successfully`
          : "Crew Coordinator deleted successfully",
      })
    );
  } catch (e) {
    console.error(e);
  }
}

// The Equasis scrape runs in a background job since CREW-1149 — the PATCH
// returns in ~0.5 s while the scrape takes seconds (or fails later). The old
// saga declared success immediately and reloaded stale data, which read as
// "нажал — сообщение что обновлено, а данные не подтянулись" (CREW-1152/1153).
// Now: announce the start, poll the completion stamp, reload when it moves.
const EQUASIS_POLL_INTERVAL_MS = 4000;
const EQUASIS_POLL_ATTEMPTS = 8; // ~32 s; the job itself typically takes 6–8 s

function* fetchItemFromEquasis() {
  try {
    yield put(actions.setEquasisLoading(true));
    const { pathname } = yield select(getLocation);
    const {
      params: { id },
    } = matchPath(pathname, { path: "/vessels/:id/:tab" });

    // `equasis_refreshed_at` moves on every successful pull (second precision),
    // so comparing against the pre-click value detects completion reliably.
    const before = yield call(Api.vessels.show, id);
    const stampBefore = get(before, "attributes.equasis_refreshed_at");

    yield call(Api.vessels.update, id, { update_from_equasis: true, updated_at: new Date() });
    yield put(
      addSuccess('Update from "Equasis.org" started — refreshing data…')
    );

    for (let attempt = 0; attempt < EQUASIS_POLL_ATTEMPTS; attempt += 1) {
      yield delay(EQUASIS_POLL_INTERVAL_MS);
      const fresh = yield call(Api.vessels.show, id);
      if (get(fresh, "attributes.equasis_refreshed_at") !== stampBefore) {
        yield put(actions.loadItemRequest());
        yield put(
          addSuccess('Vessel data from "Equasis.org" updated successfully')
        );
        yield put(actions.setEquasisLoading(false));
        return;
      }
    }

    // The job outlived the polling window (slow scrape, retries, or the
    // parser is down). Data will still land in the background if it recovers.
    yield put(actions.loadItemRequest());
    yield put(
      addError(
        'Update from "Equasis.org" is still running in the background — refresh the page in a minute.',
        "Still updating"
      )
    );
    yield put(actions.setEquasisLoading(false));
  } catch (e) {
    yield put(actions.setEquasisLoading(false));
    const apiError = get(e, "apiErrors[0]");
    if (get(apiError, "status") === "422") {
      // BE rejects the refresh when the client has no Equasis credentials.
      yield put(
        addError(get(apiError, "title") || "Set your Equasis login in the profile to update vessels.")
      );
    } else if (get(apiError, "status") === "500") {
      yield put(
        addError("Updating failed. Equasis login or password is wrong.")
      );
    } else {
      yield put(actions.loadItemsError(e));
    }
    console.error(e);
  }
}

// CREW-1300 — opening a vessel without a photo queues the fetch in the
// background (VesselsController#show), so the page that asked loaded before
// the photo existed and showed it only after a manual reload. While the
// backend says `photo_pending`, ask for the photo fields until it is there or
// the source turned out to have none. Up to a minute: the job runs on the
// parser worker, behind whatever Equasis scrapes are queued. Restarted by every
// load of the vessel (takeLatest), and dropped once the user leaves it.
export const PHOTO_POLL_INTERVAL_MS = 3000;
export const PHOTO_POLL_ATTEMPTS = 20;

export function* waitForPhoto({ payload: vessel }) {
  if (!get(vessel, "attributes.photo_pending")) return;

  const { id } = vessel;
  try {
    for (let attempt = 0; attempt < PHOTO_POLL_ATTEMPTS; attempt += 1) {
      yield delay(PHOTO_POLL_INTERVAL_MS);

      const current = yield select((s) => s.vesselDetails.data);
      if (String(current?.id) !== String(id)) return;

      const photo = yield call(Api.vessels.photo, id);
      if (photo.photo || !photo.photo_pending) {
        yield put(actions.photoArrived({ id, ...photo }));
        return;
      }
    }
  } catch (e) {
    console.error(e);
  }

  // Gave up (slow worker, or the source failed and will be retried later):
  // stop saying a photo is on its way. The next visit asks again.
  yield put(actions.photoArrived({ id, photo_pending: false }));
}

/**
 * getFeatureSagas
 * each saga file should return all feature sagas
 * @returns {ForkEffect[]}
 */
export default function getFeatureSagas() {
  return [
    takeEvery(
      [
        constants.LOAD_ITEM_REQUEST,
        constants.MANAGER_CHANGED_SUCCESSFULLY,
        constants.OWNER_CHANGED_SUCCESSFULLY,
      ],
      fetchItem
    ),
    takeEvery(constants.START_CHANGING_MANAGER, setManager),
    takeEvery(constants.START_CHANGING_OWNER, setOwner),
    takeEvery(LOCATION_CHANGE, fetchItemsPageInit),
    takeEvery(constants.FETCH_FROM_EQUASIS, fetchItemFromEquasis),
    takeEvery(constants.CHANGE_COORDINATOR, changeCoordinator),
    takeEvery(constants.UPDATE_PHOTO, updatePhoto),
    takeLatest(constants.ADD_LOADED_ITEM, waitForPhoto),
  ];
}
